import React, { createElement, FunctionComponent, useCallback, useState } from 'react';
import IComment from '@interfaces/IComment';
import { Comment as AntComment, Image } from 'antd';
import { Link } from 'react-router-dom';
import { getPath } from '@helpers/navigation';
import Avatar from '@components/Avatar';
import { FULL_TIME_FORMAT } from '@config/format';
import moment from 'moment';
import componentContent from './Comment.content.json';
import useTranslate from '@hooks/useTranslate';
import comments, { block, getComments, unblock } from '@stores/comments';
import { useStoreMap, useStore } from 'effector-react';
import { LoadingOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import useModal from '@hooks/useModal';
import { FileType } from '@interfaces/MediaFile';
import SpamPanel from '@components/SpamPanel';
import css from './Comment.module.less';
import cn from 'classnames';
import ImageModal from '@components/FullImageModal/FullImageModal';
import { IImage } from '@interfaces/IImage';

interface CommentProps {
  comment: IComment;
  mode?: 'default' | 'spam';
}

const renderCommentText = (comment: IComment) => {
  if (comment.gifUrl) {
    return (
      <>
        <a href={comment.gifUrl}>{comment.gifUrl}</a>
        <br />
        <Image
          src={comment.gifUrl}
          width={120}
        />
      </>
    );
  }

  if (Array.isArray(comment.content) && comment.content.length) {
    const firstGif = comment.content.find((c) => c.gifUrl);

    if (firstGif?.gifUrl) {
      return (
        <>
          <a href={firstGif.gifUrl}>{firstGif.gifUrl}</a>
          <br />
          <Image
            src={firstGif.gifUrl}
            width={120}
          />
        </>
      );
    }

    const text = comment.content.map((c) => c.text ?? '').join('');
    return text;
  }

  return comment.text;
};

const Comment: FunctionComponent<CommentProps> = ({ comment, mode }) => {
  const { store, loading } = useStoreMap({
    store: comments,
    keys: [comment.id],
    fn: (comments, [commentId]) => {
      return {
        store: comments.list[commentId] || null,
        loading: comments.preloaders.getComments === commentId || false,
      };
    },
  });
  const [currentImage, setCurrentImage] = useState<IImage | null>(null);

  const { preloaders } = useStore(comments);

  const [isVisible, openModal, closeModal] = useModal();

  const content = useTranslate(componentContent);

  const remainingComments = store
    ? store.pagination.total - (store.pagination.current + 1) * store.pagination.pageSize
    : comment.childrenComments;

  const isBlocking = preloaders.block === `block_${comment.id}`;
  const isUnblocking = preloaders.unblock === `unblock_${comment.id}`;

  const blockComment = useCallback(() => {
    !isBlocking &&
      block({
        commentId: comment.id,
        preloaderId: `block_${comment.id}`,
      });
  }, [isBlocking, comment.id]);

  const unblockComment = useCallback(() => {
    !isUnblocking &&
      unblock({
        commentId: comment.id,
        preloaderId: `unblock_${comment.id}`,
      });
  }, [isUnblocking, comment.id]);

  const openModalHandler = useCallback(
    (image) => {
      setCurrentImage(image);
      openModal();
    },
    [openModal],
  );

  const actions = useCallback(
    (comment: IComment) => [
      <div
        className={css.actionContainer}
        key='buttons'
      >
        <>
          {mode !== 'spam' && (
            <div
              className={cn(css.clickable, css.actionContainer)}
              onClick={() => (comment.isBlocked ? unblockComment() : blockComment())}
            >
              <span className={css.actionContainer}>
                {createElement(
                  isBlocking || isUnblocking
                    ? LoadingOutlined
                    : comment.isBlocked
                    ? EyeOutlined
                    : EyeInvisibleOutlined,
                )}
              </span>
              <span className={css.actionLabel}>
                {comment.isBlocked ? content.unblock : content.block}
              </span>
            </div>
          )}
          {mode === 'spam' && (
            <div className={css.actionContainer}>
              <Link
                className={cn(css.actionLink, css.actionLabel)}
                to={getPath('postDetail', { postId: comment.postId })}
              >
                {content.goToPost}
              </Link>
            </div>
          )}
        </>
      </div>,
    ],
    [isBlocking, isUnblocking, content, mode, blockComment, unblockComment],
  );

  return (
    <>
      <AntComment
        key={comment.id}
        actions={actions(comment)}
        className={cn({
          [css.blocked]: comment.isBlocked,
        })}
        author={
          <Link
            to={getPath('userDetail', { userId: comment.author.id })}
            className='clickable'
          >
            {comment.author.nickname}
          </Link>
        }
        avatar={<Avatar user={comment.author} />}
        content={
          <div>
            <div className={css.body}>
              {comment.image && (
                <>
                  <img
                    onClick={() => openModalHandler(comment.image)}
                    className={css.image}
                    src={comment.image.url}
                    alt=''
                  />
                </>
              )}
              <div>
                <span>{renderCommentText(comment)}</span>
              </div>
            </div>
            {mode === 'spam' ? (
              <div className={css.spamPanel}>
                <SpamPanel
                  isBlocking={!!isBlocking}
                  isUnblocking={!!isUnblocking}
                  onBlock={blockComment}
                  onUnblock={unblockComment}
                  item={comment}
                  mode='comment'
                />
              </div>
            ) : null}
          </div>
        }
        datetime={moment(comment.whenCommented).format(FULL_TIME_FORMAT)}
      >
        {store
          ? store.list.map((subcomment: IComment) => (
              <AntComment
                key={subcomment.id}
                className={cn({
                  [css.blocked]: subcomment.isBlocked,
                })}
                author={
                  <Link
                    to={getPath('userDetail', { userId: subcomment.author.id })}
                    className='clickable'
                  >
                    {subcomment.author.nickname}
                  </Link>
                }
                avatar={<Avatar user={subcomment.author} />}
                actions={actions(subcomment)}
                content={
                  <div className={css.body}>
                    {subcomment.image && (
                      <>
                        <img
                          onClick={() => openModalHandler(subcomment.image)}
                          className={css.image}
                          src={subcomment.image.url}
                          alt=''
                        />
                      </>
                    )}
                    <div>
                      <span>{renderCommentText(subcomment)}</span>
                    </div>
                  </div>
                }
                datetime={moment(subcomment.whenCommented).format(FULL_TIME_FORMAT)}
              />
            ))
          : null}
        {remainingComments && remainingComments > 0 ? (
          loading ? (
            <LoadingOutlined />
          ) : (
            <span
              className='clickable'
              onClick={() => getComments({ commentId: comment.id, preloaderId: comment.id })}
            >
              {content.show} {remainingComments} {content.answers}
            </span>
          )
        ) : null}
      </AntComment>
      {isVisible ? (
        <ImageModal
          onClose={closeModal}
          files={[
            {
              fileType: FileType.Image,
              image: currentImage,
            },
          ]}
        />
      ) : null}
    </>
  );
};

export default Comment;
